import { SnakeCasedPropertiesDeep } from "type-fest";

import * as LicenseRepository from "./licenseRepository";
import {
    BLANCCO_TOKEN_ID,
    createLicenseTypes,
    FEATURE_LICENSES,
    isSubscriptionLicense,
    OEM_LICENSE_ID,
} from "components/licenses/common";
import { EditDeliveryForm } from "components/licenses/delivery-history/DeliveryHistoryMenuItems";
import { EntitlementRequestLicense } from "components/licenses/entitlements/add-entitlement/AddEntitlementWrapper";
import { fetchFeaturesWithAvailableLicense } from "components/licenses/license-configuration/bms/bmsUtils";
import { deduceTierLicenses, isTokenLicense } from "components/tenants/add-tenant/AddTenantForm";
import { LicenseKeyFilter } from "domain/licenseKeys";
import {
    AvailableLicenseData,
    Configuration,
    ConfigurationDto,
    ConfigureData,
    Container,
    ContainerCursor,
    ContainerData,
    ContainerType,
    DeliveryHistories,
    DeliveryHistory,
    DeliveryType,
    EmsConfiguration,
    EmsConfigurationDto,
    Entitlement,
    EntitlementCursor,
    EntitlementData,
    EntitlementLicenseDto,
    EntitlementState,
    EntitlementType,
    Key,
    KeyCursor,
    KeyData,
    LicenseData,
    LicenseDelivery,
    LicenseDeliveryHistory,
    LicenseKeyCursor,
    LicenseList,
    toEmsConfiguration,
    TokenRates,
} from "domain/licenses";
import { CombinedTier, isMobileRetail, TenantType } from "domain/tenants";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import {
    deriveTenantUuid,
    getCurrentTenantDetails,
    isLicensingModelBundleWithToken,
} from "services/tenants/tenantCookieService";
import { TenantLicense } from "services/tenants/TenantService";
import { ParameterQuery } from "utils/commonFunctions";
import { isExpired } from "utils/format";
import { logger } from "utils/logging";

const BMS_LICENSES = ["bms-validation", "bms-bbti", "bms-lease", "bms-insurance", "usdk", "bms-ntf", "bms-all"];
const TOKEN_SCALER = 100;

export interface LicenseDto {
    product: string;
    license: string;
    available: number;
    total: number;
    expiration_date: string;
    type: string;
    license_type: string;
    feature_license_present: boolean;
    assigned: number;
    overall_remaining?: number;
    pool_name?: string;
    recursive_balance?: number;
    assignment_mode?: string;
}

export interface LicenseResponseDto {
    licenses: LicenseDto[];
    parent_licenses: TenantLicense[];
}

export interface LicenseResponse {
    licenses: LicenseData[];
    parentLicenses: TenantLicense[];
}

export interface TokenRatesDto {
    tokenRates: TokenRates[];
}

function toLicensesResponse(dto: LicenseResponseDto): LicenseResponse {
    return {
        licenses: dto.licenses.map(toLicenseData),
        parentLicenses: dto.parent_licenses,
    };
}

export function toLicenseData(dto: LicenseDto): LicenseData {
    return {
        product: dto.product,
        license: dto.license,
        available: dto.available,
        total: dto.total,
        expirationDate: dto.expiration_date,
        type: dto.type,
        licenseType: dto.license_type,
        isInvalid: isInvalidLicense(dto.available, dto.expiration_date),
        isFeatureLicensePresent: dto.feature_license_present,
        assigned: dto.assigned,
        overallRemaining: dto.overall_remaining,
        poolName: dto.pool_name,
        recursiveBalance: dto.recursive_balance,
        deliveryMode: dto.assignment_mode,
    };
}

export interface LicenseKey {
    licenseKeys: Key[];
}

export interface ConfigurationData {
    configuration: ConfigureData[];
}

export interface CommonDto {
    start_date: string;
    expiration_date: string;
}

export interface KeyDto extends CommonDto {
    identifier: string;
    license_key: string;
    assigned: number;
    used: number;
    remaining: number;
    usage_hours_limit: number;
    license_id: string;
    short_url: string;
    pool_name: string;
    key: string;
    enabled?: boolean;
    checked: boolean;
}

export interface LicenseDataDto extends CommonDto {
    product: string;
    license: string;
    available: number;
    total: number;
    expiration_date: string;
    type: string;
    license_type: string;
    feature_license_present: boolean;
    overall_remaining?: number;
    assigned: number;
    poolName?: string;
}

export interface ProductRateListDto {
    product_id: string;
    rate: string;
}

export interface ProductRateList {
    productId: string;
    rate: number;
}

export interface TierLicenseDto {
    tier: string;
    common_licenses: string[] | null;
    add_ons: string[] | null;
    features: string[] | null;
    migrated_to_common_licenses: string[] | null;
}

export interface TierLicense {
    tier: string;
    commonLicenses: string[];
    addOns: string[];
    features: string[];
    migratedToCommonLicenses: string[];
}

export interface TierLicensesResponse {
    tierLicenses: TierLicense[];
}

export interface KeyCursorDto {
    license_key: string;
    tenant_identifier: string;
    created: string;
}

export interface LicensesCursorDto {
    identifier: string;
    tenant_identifier: string;
    created: string;
}

export interface KeysDto {
    licenseKeys: KeyDto[];
    cursor: KeyCursorDto;
    available: boolean;
}

export interface LicensesDto {
    licenses: LicenseDataDto[];
    cursor: LicensesCursorDto;
}

export interface ProductToRateDto {
    name: string;
    uuid: string;
    product_to_rate: ProductRateListDto[];
    creation_date: string;
    rates_flag: string;
}

export interface ProductToRateList {
    name: string;
    uuid: string;
    productToRate: ProductRateList[];
    creationDate: string;
    ratesFlag: string;
}

export interface KeyResponseDto {
    licenseKeys: KeysDto;
}

export interface EntitlementCursorDto {
    last_tenant_uuid: string;
    last_entitlement_id: string;
    last_created: string;
}

export interface EntitlementDto {
    entitlement_id: string;
    created: string;
    description: string;
    backup_uuid: string;
    container_id: string;
    tenant_uuid: string;
    type: EntitlementType;
    mc_name: string;
    licenses: EntitlementLicenseDto[] | null;
    state: EntitlementState | null;
    update_date?: string;
}

export interface EntitlementsDto {
    entitlements: EntitlementDto[];
    cursor: EntitlementCursorDto;
}

export interface EntitlementResponseDto {
    entitlements: EntitlementsDto;
    count: number;
}

function toEntitlement(dto: EntitlementDto): Entitlement {
    return {
        entitlementId: dto.entitlement_id,
        created: dto.created,
        description: dto.description,
        backupUuid: dto.backup_uuid,
        containerId: dto.container_id,
        tenantUuid: dto.tenant_uuid,
        type: dto.type,
        mcName: dto.mc_name,
        licenses: dto.licenses,
        state: dto.state,
        updateDate: dto.update_date,
    };
}

function toEntitlementCursor(dto: EntitlementCursorDto): EntitlementCursor | null {
    return dto == null
        ? null
        : {
              lastTenantUuid: dto.last_tenant_uuid,
              lastEntitlementId: dto.last_entitlement_id,
              lastCreated: dto.last_created,
          };
}

export interface ContainerCursorDto {
    last_tenant_uuid: string;
    last_container_id: string;
}

export interface ContainerDto {
    container_id: string;
    tenant_uuid: string;
    date: string;
    name: string;
    type: ContainerType;
}

export interface ContainersDto {
    containers: ContainerDto[];
    cursor: ContainerCursorDto;
}

export interface ContainersResponseDto {
    containers: ContainersDto;
}

function toContainer(dto: ContainerDto): Container {
    return {
        containerId: dto.container_id,
        tenantUuid: dto.tenant_uuid,
        date: dto.date,
        name: dto.name,
        type: dto.type,
    };
}

function toContainerCursor(dto: ContainerCursorDto): ContainerCursor | null {
    return dto == null
        ? null
        : {
              lastTenantUuid: dto.last_tenant_uuid,
              lastContainerId: dto.last_container_id,
          };
}

function toLicense(dto: LicenseDataDto): LicenseData {
    return {
        product: dto.product,
        license: dto.license,
        available: dto.available,
        total: dto.total,
        expirationDate: dto.expiration_date,
        type: dto.type,
        licenseType: dto.license_type,
        isInvalid: isInvalidLicense(dto.available, dto.expiration_date),
        isFeatureLicensePresent: dto.feature_license_present,
        overallRemaining: dto.overall_remaining,
        assigned: dto.assigned,
        poolName: dto.poolName,
    };
}

function toProductToRateResponse(dto: ProductToRateDto): ProductToRateList {
    return {
        name: dto.name,
        uuid: dto.uuid,
        creationDate: dto.creation_date,
        ratesFlag: dto.rates_flag,
        productToRate: dto.product_to_rate.map((each) => {
            return {
                productId: each.product_id,
                rate: Number(each.rate) / TOKEN_SCALER,
            };
        }),
    };
}

const isInvalidLicense = (available: number, expiration: string): boolean => {
    return available <= 0 || isExpired(expiration);
};

function toKey(dto: KeyDto): Key {
    return {
        licenseKey: dto.license_key,
        identifier: dto.identifier,
        assigned: dto.assigned,
        used: dto.used,
        remaining: dto.remaining,
        startDate: dto.start_date,
        expirationDate: dto.expiration_date,
        usageHoursLimit: dto.usage_hours_limit,
        licenseType: dto.license_id,
        shortUrl: dto.short_url,
        poolName: dto.pool_name,
        enabled: dto.enabled,
        checked: dto.checked,
    };
}

function toKeyCursor(dto: KeyCursorDto): KeyCursor | null {
    return dto == null
        ? null
        : {
              licenseKey: dto.license_key,
              tenantIdentifier: dto.tenant_identifier,
              created: dto.created,
          };
}

function toLicenseCursor(dto: LicensesCursorDto): LicenseKeyCursor | null {
    return dto == null
        ? null
        : {
              identifier: dto.identifier,
              tenantIdentifier: dto.tenant_identifier,
              created: dto.created,
          };
}

function toConfiguration(dto: ConfigurationDto): Configuration {
    return {
        tenantConfiguration: dto.tenant_configuration,
        bbtiConfiguration: dto.bbti_configuration,
        validationConfiguration: dto.validation_configuration,
        insuranceConfiguration: dto.insurance_configuration,
        ntfConfiguration: dto.ntf_configuration,
        leaseConfiguration: dto.lease_configuration,
        downloadAppPageConfiguration: dto.download_app_page_configuration,
        identityVerificationConfiguration: dto.identity_verification_configuration,
        sdkConfiguration: dto.sdk_configuration,
    };
}

export interface LicenseKeyAssignment {
    licenseKey?: string;
    keyCount?: number;
    association?: string;
    identifier?: string;
    hours?: number;
    licenseType: string;
    available: number;
    assignCount: number;
    startDate: string;
    expirationDate: string;
    enabled?: boolean;
}

interface LicenseKeyAssignmentDto extends CommonDto {
    license_key?: string;
    key_count?: number;
    identifier?: string;
    hours?: number;
    assign_count: number;
    license_id: string;
    enabled?: boolean;
}

interface UpdateLicenseKeyDto extends CommonDto {
    identifier?: string;
    hours?: number;
    assign_count: number;
    license_id: string;
    enabled?: boolean;
}

interface RequestLicenseDto {
    blancco_product_id: number;
    license_amount: number;
}

function toRequestLicense(dto: EntitlementRequestLicense): RequestLicenseDto {
    return {
        blancco_product_id: dto.blanccoProductId,
        license_amount: dto.licenseAmount,
    };
}

export interface LicenseDeliveryDto {
    type: DeliveryType;
    case_number: string;
    opportunity_id: string;
    notes: string;
    tenant_uuid?: string;
    rate_uuid?: string;
    to_populate_pools?: boolean;
    target_date?: string;
    edit_date?: string;
    editor_uuid?: string;
    license_delivery_uuid?: string;
    status?: string;
    licenses: {
        license_id: string;
        amount: number;
        expiration_date: string;
        assignment_mode?: string;
    }[];
    assignment_mode?: string;
    creation_date?: string;
}

export function toLicenseDelivery(delivery: LicenseDelivery | null): LicenseDeliveryDto | null {
    if (delivery === null) {
        return null;
    }
    return {
        type: delivery.type,
        case_number: delivery.caseNumber,
        opportunity_id: delivery.opportunityId,
        notes: delivery.notes,
        licenses: delivery.licenses.map((each) => {
            return {
                license_id: each.licenseId,
                amount: each.amount,
                expiration_date: each.expirationDate,
                assignment_mode: each.deliveryMode,
            };
        }),
        tenant_uuid: delivery.tenantUuid ?? delivery.tenantUuid,
        rate_uuid: delivery.tokenRateVersion,
        to_populate_pools: delivery.toPopulatePools,
        target_date: delivery.targetDate,
        edit_date: delivery.editDate ?? delivery.editDate,
        editor_uuid: delivery.editorUuid,
        license_delivery_uuid: delivery.licenseDeliveryUuid,
        status: delivery.status,
        assignment_mode: delivery.deliveryMode,
        creation_date: delivery.creationDate,
    };
}

export interface EditDeliveryFormDto {
    case_number: string;
    opportunity_id: string;
    notes: string;
    status?: string;
    target_date?: string;
}

function toEditLicenseDeliveryDto(delivery: EditDeliveryForm): EditDeliveryFormDto {
    const dto: EditDeliveryFormDto = {
        case_number: delivery.caseNumber,
        opportunity_id: delivery.opportunityId,
        notes: delivery.notes,
    };

    if (delivery.status !== undefined) {
        dto.status = delivery.status;
    }
    if (delivery.targetDate !== undefined) {
        dto.target_date = delivery.targetDate;
    }

    return dto;
}

interface LicenseDeliveryHistoryDto {
    type: DeliveryType;
    case_number: string;
    opportunity_id: string;
    uuid: string;
    notes: string;
    creation_date: string;
    creator_uuid: string;
    editor_uuid: string;
    edit_date: string;
    rate_uuid: string;
    deliveryStatus?: string;
    targetDate?: string;
    licenses: {
        license_id: string;
        amount: number;
        expiration_date: string;
        assignment_mode?: string;
    }[];
    status: string;
}

function toLicenseDeliveryHistoryDto(delivery: LicenseDeliveryHistoryDto): LicenseDeliveryHistory {
    return {
        type: delivery.type,
        caseNumber: delivery.case_number,
        opportunityId: delivery.opportunity_id,
        notes: delivery.notes,
        creationDate: delivery.creation_date,
        creatorUuid: delivery.creator_uuid,
        editorUuid: delivery.editor_uuid,
        editDate: delivery.edit_date,
        licenseRateVersion: delivery.rate_uuid,
        uuid: delivery.uuid,
        status: delivery.deliveryStatus,
        targetDate: delivery.targetDate,
        licenses: delivery.licenses.map((each) => {
            return {
                licenseId: each.license_id,
                amount: each.amount,
                expirationDate: each.expiration_date,
                totalOfLicenses: each.amount,
                deliveryMode: each.assignment_mode,
            };
        }),
    };
}

export interface DeliveryHistoryDto {
    uuid: string;
    creation_date: string;
    notes: string;
    type: string;
    rate_uuid: string;
    status?: string;
    target_date?: string;
    editor_username?: string;
    edit_date?: string;
}

export interface DeliveryHistoryResponseDto {
    deliveryHistory: DeliveryHistoryDto[];
    count: number;
    cursor: string;
}

function toDeliveryHistory(dto: DeliveryHistoryDto): DeliveryHistory {
    return {
        uuid: dto.uuid,
        type: dto.type,
        notes: dto.notes,
        creationDate: dto.creation_date,
        licenseRateVersion: dto.rate_uuid,
        deliveryStatus: dto.status,
        deliverySchedule: dto.target_date,
        editorUsername: dto.editor_username,
        editDate: dto.edit_date,
    };
}

interface EmsConfigurationExistsResponse {
    exists: boolean;
}

type AvailableLicenseDataDto = SnakeCasedPropertiesDeep<AvailableLicenseData>;

const PATH_LICENSES = "/api/licenses";
const PATH_LICENSE_KEYS = "/license-keys";
const PATH_TOKEN_RATES = "/api/v1/token-rates";
const VERSION_PATH_LICENSE_KEYS = "/api/v1/license-keys";
const PATH_TIER_LICENSES = "/api/v1/tier-licenses";
const PATH_CLONE_DELIVERIES = "/api/v1/license-deliveries/";

class LicenseService {
    public fetchTokenRates(abortController: AbortController): Promise<ProductToRateList[]> {
        return apiGatewayService
            .invokeApi(PATH_TOKEN_RATES, "GET", null, { abortSignal: abortController.signal, apiType: ApiType.LAUREL })
            .then((dto: ProductToRateDto[]) => {
                return dto.map((eachDto) => toProductToRateResponse(eachDto));
            });
    }

    public async fetchTokenRateForTenantAndProduct(
        abortController: AbortController,
        productId: string,
        tenantUuid: string
    ) {
        const rates = await this.fetchProductTokenRate(abortController, tenantUuid);
        return rates[0]?.productToRate?.find((product) => product.productId === productId)?.rate;
    }

    public fetchProductTokenRate(
        abortController: AbortController,
        tenant_uuid?: string,
        uuid?: string
    ): Promise<ProductToRateList[]> {
        const query = new ParameterQuery().add("tenant_uuid", tenant_uuid).add("uuid", uuid).createQueryString();
        return apiGatewayService
            .invokeApi(`${PATH_TOKEN_RATES}${query}`, "GET", null, {
                abortSignal: abortController.signal,
                apiType: ApiType.LAUREL,
            })
            .then((dto: ProductToRateDto[]) => {
                return dto.map((eachDto) => toProductToRateResponse(eachDto));
            });
    }

    public fetchLicenses(
        abortController?: AbortController,
        search?: string,
        own = false,
        tenant_uuid?: string,
        pool_uuid?: string,
        all_pools?: boolean,
        recursive?: boolean
    ): Promise<LicenseResponse> {
        const query = new ParameterQuery()
            .add("search", search)
            .add("all", own ? "false" : "true")
            .add("tenant_uuid", tenant_uuid)
            .add("pool_uuid", pool_uuid)
            .add("all_pools", all_pools ? "true" : "false")
            .add("recursive", recursive ? "true" : "false")
            .createQueryString();
        return apiGatewayService
            .invokeApi(`${PATH_LICENSES}${query}`, "GET", null, { abortSignal: abortController?.signal })
            .then((dto: LicenseResponseDto) => toLicensesResponse(dto));
    }

    public fetchFromAllPools(abortController: AbortController, tenantUuid: string): Promise<LicenseResponse> {
        const query = new ParameterQuery().add("all_pools", "true").add("tenant_uuid", tenantUuid).createQueryString();
        return apiGatewayService
            .invokeApi(`${PATH_LICENSES}${query}`, "GET", null, { abortSignal: abortController.signal })
            .then((dto: LicenseResponseDto) => toLicensesResponse(dto));
    }

    public fetchAllLicenses({
        abortController,
        identifier,
        tenantIdentifier,
        search,
        own = true,
    }: {
        abortController?: AbortController;
        identifier?: string;
        tenantIdentifier?: string;
        search?: string;
        own?: boolean;
    } = {}): Promise<LicenseList> {
        const url =
            PATH_LICENSES +
            new ParameterQuery()
                .add("search", search)
                .addAll([
                    ["identifier", identifier],
                    ["tenant_identifier", tenantIdentifier],
                ])
                .add("all", own ? "false" : "true")
                .createQueryString();
        return apiGatewayService
            .invokeApi(url, "GET", null, {
                abortSignal: abortController?.signal,
            })
            .then((response: LicensesDto) => {
                const licenseData = response.licenses.map(toLicense);
                const cursor = toLicenseCursor(response.cursor);
                return {
                    licenses: {
                        licenseData: licenseData,
                        cursor: cursor,
                    },
                };
            });
    }

    public fetchLicenseKeys(
        abortController: AbortController,
        type: string,
        licenseKey?: string,
        tenantIdentifier?: string,
        created?: string,
        search?: string,
        filterType?: LicenseKeyFilter
    ): Promise<KeyData> {
        const url =
            PATH_LICENSE_KEYS +
            new ParameterQuery()
                .add("search", search)
                .add("type", type)
                .add("filter", filterType)
                .addAll([
                    ["license_key", licenseKey],
                    ["tenant_identifier", tenantIdentifier],
                    ["created", created],
                ])
                .createQueryString();
        return apiGatewayService
            .invokeApi(url, "GET", null, {
                abortSignal: abortController.signal,
            })
            .then((response: KeysDto) => {
                const licenseKeys = response.licenseKeys.map(toKey);
                const cursor = toKeyCursor(response.cursor);
                return {
                    licenseKeys: {
                        licenseKeys: licenseKeys,
                        cursor: cursor,
                    },
                };
            });
    }

    public checkLicenseKeyAvailability(
        abortController: AbortController,
        licenseKeyToCreate?: string
    ): Promise<KeyData> {
        const url = PATH_LICENSE_KEYS + new ParameterQuery().add("key", licenseKeyToCreate).createQueryString();
        return apiGatewayService
            .invokeApi(url, "GET", null, {
                abortSignal: abortController.signal,
            })
            .then((response: KeysDto) => {
                return {
                    licenseKeys: {
                        available: response.available,
                    },
                };
            });
    }

    public updateLicenseKey(
        uuid: string,
        assignment: LicenseKeyAssignment,
        abortController?: AbortController
    ): Promise<void> {
        const dto: UpdateLicenseKeyDto = {
            identifier: assignment.identifier,
            hours: assignment.licenseType === OEM_LICENSE_ID ? 0 : assignment.hours,
            assign_count: assignment.assignCount,
            start_date: assignment.startDate,
            expiration_date: assignment.expirationDate,
            license_id: assignment.licenseType,
        };
        if (assignment.licenseType === OEM_LICENSE_ID) {
            dto.enabled = assignment.enabled;
        }
        return apiGatewayService.invokeApi(VERSION_PATH_LICENSE_KEYS + "/" + uuid, "POST", dto, {
            abortSignal: abortController?.signal,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    public assignLicenseKey(assignment: LicenseKeyAssignment, abortController?: AbortController): Promise<void> {
        const dto: LicenseKeyAssignmentDto = {
            license_key: assignment.licenseKey,
            key_count: assignment.keyCount,
            identifier: assignment.identifier,
            hours: assignment.hours,
            assign_count: assignment.assignCount,
            start_date: assignment.startDate,
            expiration_date: assignment.expirationDate,
            license_id: assignment.licenseType,
        };
        if (assignment.licenseType === OEM_LICENSE_ID) {
            dto.enabled = true;
        }
        return apiGatewayService.invokeApi("/license-keys", "POST", dto, { abortSignal: abortController?.signal });
    }

    public assignBulkLicenseKeys(csv: File, abortController?: AbortController): Promise<void> {
        const formData = new FormData();
        formData.append("bulk_keys", csv);
        return apiGatewayService.invokeApi("/license-keys", "POST", formData, {
            abortSignal: abortController?.signal,
            headers: {},
            jsonResponse: false,
        });
    }

    public deleteLicenseKeys(licenseKeys: string[], abortController?: AbortController): Promise<void> {
        const body = { license_keys: licenseKeys };
        return apiGatewayService.invokeApi(VERSION_PATH_LICENSE_KEYS, "DELETE", body, {
            abortSignal: abortController?.signal,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    public updateLicenseKeysStatus(keys: string[], enabled: boolean, abortController?: AbortController): Promise<void> {
        const body = { keys: keys, enabled };
        return apiGatewayService.invokeApi(VERSION_PATH_LICENSE_KEYS + "/enable", "POST", body, {
            abortSignal: abortController?.signal,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    public cloneLicenseDelivery(
        deliveryUuid: string,
        deliveryInterval: string,
        clones: string,
        abortController: AbortController
    ): Promise<void> {
        const body = {
            desired_interval: Number(deliveryInterval),
            clones: Number(clones),
        };
        return apiGatewayService.invokeApi(PATH_CLONE_DELIVERIES + deliveryUuid + "/clone", "POST", body, {
            abortSignal: abortController.signal,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    public addConfiguration(configuration: Configuration, abortController: AbortController): Promise<void> {
        const dto: ConfigurationDto = {
            tenant_configuration: configuration.tenantConfiguration,
            bbti_configuration: configuration.bbtiConfiguration,
            validation_configuration: configuration.validationConfiguration,
            insurance_configuration: configuration.insuranceConfiguration,
            ntf_configuration: configuration.ntfConfiguration,
            lease_configuration: configuration.leaseConfiguration,
            download_app_page_configuration: configuration.downloadAppPageConfiguration,
            identity_verification_configuration: configuration.identityVerificationConfiguration,
            sdk_configuration: configuration.sdkConfiguration,
        };
        return apiGatewayService.invokeApi("/client-configurations", "POST", dto, {
            abortSignal: abortController.signal,
        });
    }

    public saveConfiguration(
        uuid: string,
        configuration: Configuration,
        abortController: AbortController
    ): Promise<void> {
        const dto: ConfigurationDto = {
            tenant_configuration: configuration.tenantConfiguration,
            bbti_configuration: configuration.bbtiConfiguration,
            validation_configuration: configuration.validationConfiguration,
            insurance_configuration: configuration.insuranceConfiguration,
            ntf_configuration: configuration.ntfConfiguration,
            lease_configuration: configuration.leaseConfiguration,
            download_app_page_configuration: configuration.downloadAppPageConfiguration,
            identity_verification_configuration: configuration.identityVerificationConfiguration,
            sdk_configuration: configuration.sdkConfiguration,
        };
        return apiGatewayService.invokeApi("/client-configurations/" + uuid, "POST", dto, {
            abortSignal: abortController.signal,
        });
    }

    public fetchConfigurationData(signal: AbortSignal): Promise<ConfigurationData> {
        return apiGatewayService.invokeApi("/client-configurations", "GET", null, { abortSignal: signal });
    }

    public fetchClientConfigurationData(uuid: string | undefined, signal: AbortSignal): Promise<Configuration> {
        return apiGatewayService
            .invokeApi("/client-configurations/" + uuid, "GET", null, {
                abortSignal: signal,
            })
            .then((dto: ConfigurationDto) => toConfiguration(dto));
    }

    public fetchDefaultConfigurationData(abortController: AbortController): Promise<Configuration> {
        return apiGatewayService
            .invokeApi("/client-configurations/default", "GET", null, {
                abortSignal: abortController.signal,
            })
            .then((dto: ConfigurationDto) => toConfiguration(dto));
    }

    public fetchAvailableLicenses(type: string, abortSignal?: AbortSignal): Promise<AvailableLicenseData> {
        return apiGatewayService
            .invokeApi("/api/licenses/" + type, "GET", null, {
                abortSignal,
            })
            .then((response: AvailableLicenseDataDto) => ({
                availableLicenses: response.available_licenses,
                expiration: response.expiration,
            }));
    }

    public fetchEntitlements(
        abortController: AbortController,
        cursor: EntitlementCursor | null,
        search?: string,
        all?: string
    ): Promise<EntitlementData> {
        const cursorDto: EntitlementCursorDto = {
            last_entitlement_id: cursor ? cursor.lastEntitlementId : "",
            last_tenant_uuid: cursor ? cursor.lastTenantUuid : "",
            last_created: cursor ? cursor.lastCreated : "",
        };
        const url =
            "/api/tenants/ems/entitlements" +
            new ParameterQuery().add("search", search).add("all", all).createQueryString();
        return apiGatewayService
            .invokeApi(url, "POST", cursor ? { cursor: cursorDto } : null, {
                abortSignal: abortController.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
            })
            .then((data: EntitlementResponseDto) => {
                const entitlements = data.entitlements.entitlements.map(toEntitlement);
                const entitlementCursor = toEntitlementCursor(data.entitlements.cursor);
                return {
                    entitlements: {
                        entitlements: entitlements,
                        cursor: entitlementCursor,
                        count: data.count,
                    },
                };
            });
    }

    public fetchAllContainersRecursively(
        abortController: AbortController,
        cursor: ContainerCursor | null,
        callCount: number
    ): Promise<Container[]> {
        // Safety switch for recursion
        if (callCount >= 5) {
            logger.error("Maximum amount of recursive container fetches reached");
            return Promise.resolve([]);
        }
        return this.fetchContainers(abortController, cursor).then((containerData) => {
            const containers = containerData.containers.containers;
            const newCursor = containerData.containers.cursor;
            if (newCursor != null) {
                return this.fetchAllContainersRecursively(abortController, newCursor, ++callCount).then(
                    (moreContainers) => {
                        return containers.concat(moreContainers);
                    }
                );
            }
            return containers;
        });
    }

    public fetchContainers(
        abortController: AbortController,
        cursor: ContainerCursor | null,
        search?: string
    ): Promise<ContainerData> {
        const cursorDto: ContainerCursorDto = {
            last_container_id: cursor ? cursor.lastContainerId : "",
            last_tenant_uuid: cursor ? cursor.lastTenantUuid : "",
        };
        const url = "/api/tenants/ems/containers" + new ParameterQuery().add("search", search).createQueryString();
        return apiGatewayService
            .invokeApi(url, "POST", cursor ? { cursor: cursorDto } : null, {
                abortSignal: abortController.signal,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                refreshSession: true,
            })
            .then((data: ContainersResponseDto) => {
                const containers = data.containers.containers.map(toContainer);
                const containerCursor = toContainerCursor(data.containers.cursor);
                return {
                    containers: {
                        containers: containers,
                        cursor: containerCursor,
                    },
                };
            });
    }

    public editEntitlement(
        entitlementId: string,
        description: string | null,
        abortController: AbortController,
        entitlementState?: EntitlementState
    ): Promise<void> {
        const postData = { description: description, entitlement_state: entitlementState };
        return apiGatewayService.invokeApi("/api/tenants/ems/entitlements/" + entitlementId, "POST", postData, {
            refreshSession: true,
            abortSignal: abortController.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
        });
    }

    public fetchEmsConfiguration(
        uuid: string,
        abortController?: AbortController,
        region?: string
    ): Promise<EmsConfiguration> {
        return apiGatewayService
            .invokeApi(`/api/tenants/${uuid}/ems/configuration`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                region: region,
            })
            .then((dto: EmsConfigurationDto) => toEmsConfiguration(dto));
    }

    public fetchEmsConfigurationExistence(
        uuid: string,
        abortController?: AbortController,
        region?: string
    ): Promise<boolean> {
        return apiGatewayService
            .invokeApi(`/api/tenants/${uuid}/ems/configuration?exists`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                region: region,
            })
            .then((dto: EmsConfigurationExistsResponse) => dto.exists);
    }

    public editContainer(containerId: string, name: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            "/api/tenants/ems/containers/" + containerId,
            "POST",
            { name: name },
            {
                refreshSession: true,
                abortSignal: abortController.signal,
                apiType: ApiType.OLIVER,
                emptyResponse: true,
            }
        );
    }

    public checkEmsCustomerIdExistence(id: string, abortController?: AbortController): Promise<boolean> {
        return apiGatewayService
            .invokeApi(`/api/ems/customer/${id}`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
                emptyResponse: true,
            })
            .then(() => Promise.resolve(true))
            .catch(() => Promise.resolve(false));
    }

    public synchronize(abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi("/api/tenants/ems/synchronize", "POST", null, {
            abortSignal: abortController?.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
        });
    }

    public createEntitlement(
        type: string,
        description: string,
        licensesList: EntitlementRequestLicense[],
        abortController: AbortController,
        containerId?: string,
        mcUuid?: string
    ): Promise<void> {
        return apiGatewayService.invokeApi(
            "/api/tenants/ems/entitlement/create",
            "POST",
            {
                type: type,
                description: description,
                licenses: licensesList.map(toRequestLicense),
                key_id: containerId,
                mc_uuid: mcUuid,
            },
            {
                refreshSession: true,
                abortSignal: abortController.signal,
                apiType: ApiType.OLIVER,
                emptyResponse: true,
            }
        );
    }

    public fetchV2cForEntitlement(entitlementId: string, abortController: AbortController): Promise<string> {
        const apiPath = `/api/tenants/ems/entitlements/${entitlementId}/v2c`;

        return apiGatewayService
            .invokeApi(apiPath, "GET", null, {
                abortSignal: abortController.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                jsonResponse: true,
            })
            .then((response: { v2c: string }) => response.v2c);
    }

    public fetchDeliveryHistory(
        abortController: AbortController,
        search?: string,
        cursor?: string,
        status?: string
    ): Promise<DeliveryHistories> {
        const url =
            "/api/license-delivery" +
            new ParameterQuery().add("search", search).add("cursor", cursor).add("status", status).createQueryString();
        return apiGatewayService
            .invokeApi(url, "GET", null, { abortSignal: abortController.signal })
            .then((dto: DeliveryHistoryResponseDto) => {
                return {
                    count: dto.count,
                    cursor: dto.cursor,
                    deliveryHistories: dto.deliveryHistory.map(toDeliveryHistory),
                };
            });
    }

    public createLicenseDelivery(
        licenseDelivery: LicenseDelivery,
        abortController: AbortController,
        region?: string
    ): Promise<void> {
        return apiGatewayService.invokeApi("/api/license-delivery", "POST", toLicenseDelivery(licenseDelivery), {
            abortSignal: abortController.signal,
            region: region,
            emptyResponse: true,
        });
    }

    public fetchLicenseDeliveryHistory(
        uuid: string,
        abortController?: AbortController
    ): Promise<LicenseDeliveryHistory> {
        return apiGatewayService
            .invokeApi(`/api/license-delivery/${uuid}`, "GET", null, { abortSignal: abortController?.signal })
            .then((dto: LicenseDeliveryHistoryDto) => toLicenseDeliveryHistoryDto(dto));
    }

    public editLicenseDelivery(
        uuid: string,
        licenseDeliveryDetails: EditDeliveryForm,
        abortController: AbortController,
        region?: string
    ): Promise<void> {
        return apiGatewayService.invokeApi(
            `/api/license-delivery/${uuid}`,
            "POST",
            toEditLicenseDeliveryDto(licenseDeliveryDetails),
            {
                abortSignal: abortController.signal,
                region: region,
            }
        );
    }

    public checkBmsLicenses(
        own: boolean,
        abortController: AbortController,
        tenantUuid: string,
        tenantTier: CombinedTier
    ): Promise<void> {
        return licenseService
            .fetchAllLicenses({ abortController, tenantIdentifier: tenantUuid, own })
            .then((licenses) => {
                fillBmsLocalStorageVariables(licenses.licenses.licenseData, tenantTier);
                fillSeriaKeyLocalStorageVariable(licenses.licenses.licenseData);
            });
    }

    private async fetchBmsLicenses(
        abortController: AbortController,
        tenantUuid: string,
        tenantType: TenantType,
        tenantTier: CombinedTier,
        own: boolean
    ) {
        const response = await licenseService.fetchAllLicenses({
            abortController,
            tenantIdentifier: tenantUuid,
            own: own,
        });
        return this.findBmsLicenses(tenantType, response.licenses.licenseData, tenantTier);
    }

    public findBmsLicenses(tenantType: TenantType, licenseData: LicenseData[], tenantTier: string) {
        const bmsLicenses: LicenseData[] = [];
        if (!isMobileRetail(tenantType)) {
            BMS_LICENSES.forEach((bmsLicense) => {
                licenseData.forEach((license) => {
                    if (license.type === bmsLicense) {
                        bmsLicenses.push(license);
                    }
                });
            });
        } else {
            const commonLicense = licenseData.find((license) => license.type === BLANCCO_TOKEN_ID);
            if (commonLicense) {
                const tierLicenses = deduceTierLicenses(tenantTier, true, true);
                if (tierLicenses) {
                    tierLicenses.forEach((each) => {
                        bmsLicenses.push({
                            ...commonLicense,
                            type: each,
                        });
                    });
                }
            }
        }
        return bmsLicenses;
    }

    public async fetchUserOwnBmsLicense(
        abortController: AbortController,
        tenantUuid: string,
        tenantType: TenantType,
        tenantTier: CombinedTier,
        own: boolean
    ): Promise<LicenseData[]> {
        return await this.fetchBmsLicenses(abortController, tenantUuid, tenantType, tenantTier, own);
    }

    public fetchFeaturesWithLicenses = (abortController: AbortController) =>
        this.fetchAllLicenses({ abortController, own: false })
            .then((licenseList) => {
                const deducedList = deduceAvailableLicenses(licenseList);
                return fetchFeaturesWithAvailableLicense(deducedList);
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to load licenses:", error);
                }
                throw error;
            });

    public fetchTierLicenses(abortController?: AbortController): Promise<TierLicensesResponse> {
        return apiGatewayService
            .invokeApi(PATH_TIER_LICENSES, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            })
            .then((dto: TierLicenseDto[]) => ({
                tierLicenses: dto.map((tierDto) => ({
                    tier: tierDto.tier,
                    commonLicenses: tierDto.common_licenses || [],
                    addOns: tierDto.add_ons || [],
                    features: tierDto.features || [],
                    migratedToCommonLicenses: tierDto.migrated_to_common_licenses || [],
                })),
            }));
    }
}

export function fillBmsLocalStorageVariables(licenseData: LicenseData[], tenantTier: CombinedTier) {
    LicenseRepository.clear();
    if (isLicensingModelBundleWithToken()) {
        const commonLicense = licenseData.find((license) => license.type === BLANCCO_TOKEN_ID);
        const tierLicenses = deduceTierLicenses(tenantTier, true, true);
        if (tierLicenses && commonLicense) {
            tierLicenses.forEach((each) => {
                licenseData.push({ ...commonLicense, type: each });
            });
        }
    }
    if (licenseData.length === 0) {
        LicenseRepository.setBmsLicenses(false);
        LicenseRepository.setOnlyBms(false);
        return;
    }
    let hasAnyBmsLicense = false;
    let hasOnlyBmsLicenses = true;
    licenseData.forEach((licenseData) => {
        if (BMS_LICENSES.includes(licenseData.type)) {
            hasAnyBmsLicense = true;
        } else {
            hasOnlyBmsLicenses = false;
        }
    });
    LicenseRepository.setBmsLicenses(hasAnyBmsLicense);
    LicenseRepository.setOnlyBms(hasOnlyBmsLicenses);
}

export function fillSeriaKeyLocalStorageVariable(licenseData: LicenseData[]) {
    LicenseRepository.clearLicenseSerialKey();
    let hasLicenseSerialKey = false;
    if (!isLicensingModelBundleWithToken()) {
        if (licenseData.length === 0) {
            LicenseRepository.setLicenseSerialKey(hasLicenseSerialKey);
            return;
        }
        if (licenseData.some((license) => license.type === OEM_LICENSE_ID)) {
            hasLicenseSerialKey = true;
        }
    } else {
        hasLicenseSerialKey = false;
    }
    LicenseRepository.setLicenseSerialKey(hasLicenseSerialKey);
}

export function extractTenantLicenseRateUuid(
    rateVersions: ProductToRateList[] | undefined,
    tenantType?: string,
    parentRateUuid?: string
): string {
    let rateUuid: string = "";
    if (parentRateUuid) {
        return parentRateUuid;
    }
    if (!rateVersions || rateVersions.length === 0) {
        return "";
    }

    for (const item of rateVersions) {
        if (item.ratesFlag === tenantType) {
            return item.uuid;
        }
        if (!rateUuid && item.ratesFlag === "BUNDLES") {
            rateUuid = item.uuid;
        }
    }
    return rateUuid;
}

export function extractProductRatesList(
    rateVersions: ProductToRateList[] | undefined,
    rateUuid: string
): ProductRateList[] {
    let productRatesList: ProductRateList[] = [];
    if (rateVersions?.length != 0) {
        rateVersions?.forEach((rate) => {
            if (rate.uuid == rateUuid) {
                productRatesList = rate.productToRate;
            }
        });
    }
    return productRatesList;
}

export async function fetchLicenseRateVersion(): Promise<ProductToRateList[]> {
    const abortController = new AbortController();
    return await licenseService.fetchTokenRates(abortController);
}

export async function fetchCurrentTenantRateVersions(tenantUuid?: string): Promise<ProductToRateList[]> {
    const abortController = new AbortController();
    if (["INTERNAL", "CHANNEL"].includes(getCurrentTenantDetails().type)) {
        return await licenseService.fetchTokenRates(abortController);
    }
    return await licenseService.fetchProductTokenRate(abortController, tenantUuid || deriveTenantUuid());
}

export const licenseService = new LicenseService();

export const fetchLicenses = (abortController: AbortController) =>
    licenseService
        .fetchAllLicenses({ abortController, own: false })
        .then((licenseList) => {
            const deducedList = deduceAvailableLicenses(licenseList);

            return fetchFeaturesWithAvailableLicense(deducedList);
        })
        .catch((error) => {
            if (!abortController.signal.aborted) {
                logger.error("Failed to load licenses:", error);
            }
            throw error;
        });

export function deduceAvailableLicenses(licenseList: LicenseList): LicenseList {
    if (isLicensingModelBundleWithToken()) {
        const bundleLicenses = deduceTierLicenses(getCurrentTenantDetails().tenantTier, true, false);
        const deducedLicenses: LicenseData[] = [];
        const allProducts = createLicenseTypes(true);
        const tokenLicense = licenseList.licenses.licenseData.find((each) => isTokenLicense(each.type));
        if (tokenLicense && bundleLicenses) {
            bundleLicenses.forEach((each) => {
                deducedLicenses.push({
                    ...tokenLicense,
                    type: each,
                    product: "",
                    licenseType: isSubscriptionLicense(each)
                        ? "subscription"
                        : FEATURE_LICENSES.includes(each)
                          ? "feature"
                          : "normal",
                    license: allProducts.find((product) => (product.productId = each))?.productName || "",
                });
            });
        }
        return {
            licenses: {
                licenseData: deducedLicenses,
                cursor: licenseList.licenses.cursor,
            },
        };
    } else {
        return licenseList;
    }
}
